import { CreatorBrand } from 'constants/brands';
import React, { useEffect } from 'react';
import { t } from '@lingui/macro';
import {
    Box,
    PlayFilled,
    PauseFilled,
    useBreakpoint,
    Group,
    IconButton,
    Flex,
    Divider,
    CaretUpOutlined,
} from '@landr/maestro';
import { cva } from '@landr/styled-system/css';
import { CreatorDownloadSource, CreatorCollectionPlaybackMeta } from 'types';
import { DownloadType, RoutePaths } from 'types/enums';
import { getLinearToDecibel } from 'helpers/audio';
import { Hidden } from 'components/CommonUi/Hidden';
import { useOnboardingContext } from 'contexts/OnboardingContext/OnboardingContext';
import { useCreatorContext } from 'controllers/business/Creator/Creator.controller';
import { CreatorSampleType } from 'controllers/business/Creator/Creator.types';
import SamplesCount from '../SamplesCount';
import { creator } from '../Creator.styled';
import { CreatorHeaderMobileMenu } from './CreatorHeaderMobileMenu/CreatorHeaderMobileMenu';
import { BpmControlPopover } from './BpmControlPopover';
import { KeyControlPopover } from './KeyControlPopover';
import { CreatorHeaderActionsGroup } from './CreatorHeaderActionsGroup';
import CreatorLogo from './CreatorLogo';

const trigger = cva({
    base: {
        color: { base: 'icon.high', _hover: 'icon.high.hover' },
        flexShrink: 0,
        transition: 'transform 0.25s ease-out',
        cursor: 'pointer',
        marginLeft: 'xl',
        marginRight: 'sm',
    },
    variants: {
        isCreatorOpen: {
            true: {
                transform: 'rotate(-180deg)',
            },
        },
    },
});

export interface CreatorHeaderProps {
    onClickPlay: () => void;
    onApplyBpm: (bmp: number) => void;
}

export const CreatorHeader: React.FC<CreatorHeaderProps> = ({
    onClickPlay,
    onApplyBpm,
}) => {
    const { state, getTrackGain } = useCreatorContext();
    const { requestOnboarding, WalkThrough } = useOnboardingContext();

    const {
        keyRoot,
        bpm,
        keyQuality,
        normalizedRootValue,
        isCreatorOpen,
        isUiPaused,
        isSessionProcessing,
        samplesInSession,
    } = state;

    const sampleIds = [...samplesInSession.keys()];
    const samples = [...samplesInSession.values()];
    const samplesCount = sampleIds.length;

    const areActionsDisabled = samplesCount === 0 || isSessionProcessing;

    const creatorDownloadSource: CreatorDownloadSource = {
        type: DownloadType.CreatorMulti,
        targetMeta: {
            targetBpm: state.bpm,
            targetKeyRoot: state.keyRoot,
            targetKeyQuality: state.keyQuality,
        },
        samples: [...samplesInSession.values()].map(
            (sample: CreatorSampleType) => ({
                guid: sample.guid,
                gainDb: getLinearToDecibel(getTrackGain(sample.guid)),
                semitoneShift: sample.semitoneShift,
                timeFactor: sample.timeFactor,
            }),
        ),
        name: 'Creator',
        isDownloaded: false,
    };

    useEffect(() => {
        if (isCreatorOpen && !areActionsDisabled) {
            requestOnboarding([WalkThrough.Creator]);
        }
    }, [
        isCreatorOpen,
        areActionsDisabled,
        requestOnboarding,
        WalkThrough.Creator,
    ]);

    let playbackMeta: CreatorCollectionPlaybackMeta | undefined = undefined;

    if (keyRoot && keyQuality) {
        playbackMeta = {
            bpm,
            keyRoot: keyRoot,
            keyQuality: keyQuality,
        };
    }

    const isSmallMobile = useBreakpoint('sm');

    const responsiveGutter = isSmallMobile ? 'xxs' : 'xs';

    const classes = creator({ isSmall: samplesCount <= 1 });

    return (
        <Box
            data-component="CreatorHeader"
            width="100%"
            className={classes.header}
        >
            <Box
                width="100%"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
            >
                <Group
                    display="flex"
                    alignItems="center"
                    gap={responsiveGutter}
                    onClick={(e: React.MouseEvent): void => e.stopPropagation()}
                >
                    <Group.Item>
                        <CreatorLogo link={`/${RoutePaths.CreatorBeatMaker}`} />
                    </Group.Item>
                    <Group.Item>
                        <IconButton
                            variant="secondary"
                            isRounded
                            size="sm"
                            icon={isUiPaused ? PlayFilled : PauseFilled}
                            isLoading={isSessionProcessing}
                            isDisabled={areActionsDisabled}
                            onClick={onClickPlay}
                            label={t`Play`}
                        />
                    </Group.Item>
                    <Group.Item>
                        <Box
                            position="relative"
                            data-component="BpmAndKeyControl"
                        >
                            <Group
                                display="flex"
                                alignItems="center"
                                gap={responsiveGutter}
                                onClick={(e: React.MouseEvent): void =>
                                    e.stopPropagation()
                                }
                            >
                                <Group.Item>
                                    <BpmControlPopover
                                        bpm={state.bpm}
                                        isDisabled={areActionsDisabled}
                                        onApply={onApplyBpm}
                                    />
                                </Group.Item>
                                <Group.Item>
                                    <KeyControlPopover
                                        keyQuality={keyQuality}
                                        normalizedRootValue={
                                            normalizedRootValue
                                        }
                                        isDisabled={areActionsDisabled}
                                    />
                                </Group.Item>
                            </Group>
                        </Box>
                    </Group.Item>
                    <Hidden as={Group.Item} at="md" marginLeft="2xs">
                        <SamplesCount samplesCount={samplesCount} />
                    </Hidden>
                </Group>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-end"
                >
                    {samplesCount > 0 && (
                        <>
                            <Flex
                                hideBelow="md"
                                alignItems="center"
                                marginRight="sm"
                                gap="xs"
                                onClick={(e: React.MouseEvent): void =>
                                    e.stopPropagation()
                                }
                            >
                                <CreatorHeaderActionsGroup
                                    creatorDownloadSource={
                                        creatorDownloadSource
                                    }
                                    samples={samples}
                                    playbackMeta={playbackMeta}
                                    isDisabled={areActionsDisabled}
                                />
                            </Flex>
                            <Flex
                                hideFrom="md"
                                alignItems="center"
                                onClick={(e: React.MouseEvent): void =>
                                    e.stopPropagation()
                                }
                            >
                                <CreatorHeaderMobileMenu
                                    samples={samples}
                                    creatorDownloadSource={
                                        creatorDownloadSource
                                    }
                                    playbackMeta={playbackMeta}
                                    isDisabled={areActionsDisabled}
                                />
                            </Flex>
                            {/* TODO: Divider not rendering vertically ? */}
                            <Divider
                                height="md"
                                width="1px"
                                backgroundColor="border.medium"
                                hideBelow="md"
                            />
                            <CaretUpOutlined
                                aria-label={t`Toggle ${CreatorBrand}`}
                                className={trigger({ isCreatorOpen })}
                            />
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default CreatorHeader;
